import { Patient, Room, PatientStatus, SurgerySubStatus, SurgeryCondition, Hospital, HospitalGroup } from '../types';

// Use relative path for API, assuming app is served from the same domain
// or configured in vite proxy. For local dev with separate ports, might need full URL.
const API_BASE = '/api';

const getAuthToken = () => {
    try {
        const authDataStr = localStorage.getItem('hospitalstatus_auth');
        if (authDataStr) {
            const authData = JSON.parse(authDataStr);
            return authData.token && authData.token !== 'session_active' ? authData.token : '';
        }
    } catch (e) {}

    try {
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('visitor_token_')) {
                return sessionStorage.getItem(key) || '';
            }
        }
    } catch (e) {}

    return '';
};

const handleAuthError = (res: Response) => {
    if (res.status === 401 || res.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:expired', { detail: { path: res.url } }));
        return true;
    }
    return false;
};

export const apiFetch = async (url: string, options: RequestInit = {}) => {
    const token = getAuthToken();
    const headers = new Headers(options.headers || {});
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    const res = await fetch(url, { ...options, headers });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    return res;
};

function createResilientSSE<T>(url: string, callback: (data: T) => void, withCreds = false) {
    let eventSource: EventSource | null = null;

    const connect = () => {
        if (eventSource) eventSource.close();
        
        const token = getAuthToken();
        const finalUrl = token ? (url.includes('?') ? `${url}&token=${token}` : `${url}?token=${token}`) : url;
        
        eventSource = new EventSource(finalUrl, withCreds ? { withCredentials: true } : undefined);

        eventSource.onmessage = (event) => {
            try {
                const parsed = JSON.parse(event.data);
                if (parsed && parsed.type === 'connected') return;
                if (parsed === null) return; // Ignore null clears
                callback(parsed);
            } catch (e) {
                console.error("SSE parse error", e);
            }
        };

        eventSource.onerror = (error) => {
            // Browser standard auto-reconnect mechanism handles exponential backoff
        };
    };

    connect();

    // The key fix: Mobile OS suspends background tabs, freezing EventSource.
    // This listener forces a fresh reconnection instantly upon unlocking/returning to app.
    const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
            connect();
        }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
        if (eventSource) eventSource.close();
        document.removeEventListener('visibilitychange', handleVisibility);
    };
}

export const subscribeToPatients = (hospitalId: string, callback: (patients: Patient[]) => void) => {
    return createResilientSSE(`${API_BASE}/patients/stream?hospitalId=${hospitalId}`, callback, true);
};

export const getPatients = async (hospitalId: string): Promise<Patient[]> => {
    const res = await apiFetch(`${API_BASE}/patients?hospitalId=${hospitalId}`, { credentials: 'include' });
    if (!res.ok) return [];
    return await res.json();
};

export const subscribeToPatient = (patientId: string, callback: (patient: Patient | null) => void) => {
    return createResilientSSE(`${API_BASE}/patients/${patientId}/stream`, callback);
};

export const getPatientById = async (patientId: string): Promise<Patient | null> => {
    const res = await fetch(`${API_BASE}/patients/${patientId}`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    return data;
};

export const getRooms = async (hospitalId: string): Promise<Room[]> => {
    const res = await apiFetch(`${API_BASE}/rooms?hospitalId=${hospitalId}`, { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json();
    return data;
};

// Hospitals
export const getHospitals = async (): Promise<Hospital[]> => {
    const res = await fetch(`${API_BASE}/hospitals`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return data;
};

export const getHospitalById = async (id: string): Promise<Hospital | null> => {
    const res = await fetch(`${API_BASE}/hospitals/${id}`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data;
};

export const dbSaveHospital = async (hospital: Hospital, forceCreate: boolean = false) => {
    const method = forceCreate ? 'POST' : 'PUT';
    const url = forceCreate ? `${API_BASE}/hospitals` : `${API_BASE}/hospitals/${hospital.id}`;

    const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(hospital)
    });
    if (handleAuthError(res)) return;
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(`dbSaveHospital failed: ${res.status} ${res.statusText} \n ${text}`);
        alert(`Error al guardar Hospital: ${text}`);
    }
};

export const dbDeleteHospital = async (id: string) => {
    const res = await apiFetch(`${API_BASE}/hospitals/${id}`, {
        method: 'DELETE',
        credentials: 'include'
    });
    if (handleAuthError(res)) return;
};

export const subscribeToHospitalConfig = (hospitalId: string, callback: (hospital: Hospital) => void) => {
    return createResilientSSE(`${API_BASE}/hospitals/${hospitalId}/stream`, callback, true);
};

export const subscribeToRooms = (hospitalId: string, callback: (rooms: Room[]) => void) => {
    return createResilientSSE(`${API_BASE}/rooms/stream?hospitalId=${hospitalId}`, callback, true);
};


// Groups
export const getHospitalGroups = async (): Promise<HospitalGroup[]> => {
    const res = await apiFetch(`${API_BASE}/groups`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return [];
    return await res.json();
};

export const dbSaveHospitalGroup = async (group: Partial<HospitalGroup>, forceCreate: boolean = false) => {
    const method = forceCreate ? 'POST' : 'PUT';
    const url = forceCreate ? `${API_BASE}/groups` : `${API_BASE}/groups/${group.id}`;

    const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(group)
    });
    if (handleAuthError(res)) return;
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        alert(`Error al guardar Cadena: ${text}`);
        throw new Error(text);
    }
};

export const dbDeleteHospitalGroup = async (id: string) => {
    const res = await apiFetch(`${API_BASE}/groups/${id}`, {
        method: 'DELETE',
        credentials: 'include'
    });
    if (handleAuthError(res)) return;
    if (!res.ok) throw new Error("Failed to delete group");
};

export const getGroupPreferences = async (groupId: string): Promise<any> => {
    const res = await apiFetch(`${API_BASE}/groups/${groupId}/preferences`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return {};
    const data = await res.json();
    return data.preferences || {};
};

export const updateGroupPreferences = async (groupId: string, preferences: any): Promise<boolean> => {
    const res = await apiFetch(`${API_BASE}/groups/${groupId}/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(preferences)
    });
    if (handleAuthError(res)) return false;
    return res.ok;
};

// Actions
export const dbAdmitPatient = async (patient: Patient): Promise<Patient | undefined> => {
    const res = await apiFetch(`${API_BASE}/patients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patient)
    });
    if (handleAuthError(res)) return undefined;
    if (!res.ok) return undefined;
    const data = await res.json();
    return data.patient;
};

export const dbUpdatePatient = async (patientId: string, updates: Partial<Patient>) => {
    const res = await apiFetch(`${API_BASE}/patients/${patientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates, (k, v) => v === undefined ? null : v)
    });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (res.status === 409) {
        throw new Error('CONFLICT: Alguien más actualizó este paciente. Por favor recarga la página para ver los últimos cambios.');
    }
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Failed to update patient: ${res.status} ${res.statusText} - ${text}`);
    }
};

export const dbDeletePatient = async (patientId: string) => {
    const res = await apiFetch(`${API_BASE}/patients/${patientId}`, {
        method: 'DELETE',
        credentials: 'include'
    });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (!res.ok) {
        throw new Error(`Failed to delete patient: ${res.statusText}`);
    }
};

export const batchUpdatePatients = async (updates: { id: string, changes: Partial<Patient> }[]): Promise<{ success: boolean, error?: string }> => {
    try {
        await Promise.all(updates.map(u => dbUpdatePatient(u.id, u.changes)));
        return { success: true };
    } catch(e: any) {
        console.error("Batch update via Promise.all failed:", e);
        return { success: false, error: e.message };
    }
};

export const dbClearRoom = async (hospitalId: string, roomId: string) => {
    // Current backend logic in `dbUpdatePatient` (PUT) handles room clearing on EXIT status.
    // If we need explicit room clearing without patient update:
    // We might need an endpoint for room updates specifically.
    // For now, let's assume the backend handles it via patient update or we add room update.
    // Let's add a specific room update if needed, or re-use patient update logic.
    // Actually, `worker/index.ts` has logic: if status is EXIT, it clears the room.
    // So we might not need this explicitly if we always set status to EXIT using dbUpdatePatient.
    // But if we want to force clear:
    // We need to implement room update in worker.
    // For now, let's assume this is handled by the patient update flows.
};

export const initializeHospitalRooms = async (hospitalId: string, roomNames: string[]) => {
    const rooms = roomNames.map(name => ({
        id: `${hospitalId}_${name.replace(/\s+/g, '_').toUpperCase()}`,
        name
    }));

    const res = await apiFetch(`${API_BASE}/rooms/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ hospitalId, rooms })
    });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (!res.ok) throw new Error("Failed to initialize rooms");
};

export const dbAddRoom = async (room: Room) => {
    const res = await apiFetch(`${API_BASE}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(room)
    });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (!res.ok) throw new Error("Failed to add room");
};

export const dbUpdateRoom = async (id: string, name: string) => {
    const res = await apiFetch(`${API_BASE}/rooms/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
    });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (!res.ok) throw new Error("Failed to update room");
};

export const dbDeleteRoom = async (id: string) => {
    const res = await apiFetch(`${API_BASE}/rooms/${id}`, {
        method: 'DELETE',
        credentials: 'include'
    });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (!res.ok) throw new Error("Failed to delete room");
};

export const getAllPatientsHistory = async (): Promise<Patient[]> => {
    const res = await apiFetch(`${API_BASE}/patients/history`, { credentials: 'include' });
    if (handleAuthError(res)) throw new Error('UNAUTHORIZED');
    if (!res.ok) {
        if (res.status === 401) throw new Error('UNAUTHORIZED');
        return [];
    }
    return await res.json();
};

export const getHospitalHistory = async (hospitalId: string): Promise<Patient[]> => {
    const res = await apiFetch(`${API_BASE}/patients/hospital-history?hospitalId=${hospitalId}`, { credentials: 'include' });
    if (!res.ok) return [];
    return await res.json();
};

export const dbLogin = async (hospitalId: string, pin: string) => {
    const res = await apiFetch(`${API_BASE}/auth/login-by-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin }) // The new endpoint only requires pin for lookup
    });
    if (!res.ok) throw new Error('Invalid credentials');
    return await res.json();
};

export const dbLogout = async () => {
    try {
        await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch(e) {}
};

export const fetchHospitalAnalytics = async (hospitalId: string, startDate?: string, endDate?: string): Promise<any | null> => {
    let url = `${API_BASE}/hospitals/${hospitalId}/analytics`;
    if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
    }
    const res = await apiFetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    return await res.json();
};

// ============================================================
// 📺 SCREEN SESSIONS — Fleet Monitoring
// ============================================================

export const getScreenSessions = async (hospitalId?: string): Promise<any[]> => {
    let url = `${API_BASE}/screens`;
    if (hospitalId) url += `?hospitalId=${hospitalId}`;
    const res = await apiFetch(url, { credentials: 'include' });
    if (!res.ok) {
        if (res.status === 401 || res.status === 403) throw new Error('UNAUTHORIZED');
        return [];
    }
    return await res.json();
};

export const deleteScreenSession = async (screenId: string): Promise<boolean> => {
    const res = await apiFetch(`${API_BASE}/screens/${screenId}`, {
        method: 'DELETE',
        credentials: 'include'
    });
    if (handleAuthError(res)) return false;
    if (!res.ok) return false;
    const data = await res.json();
    return data.success;
};

export const sendScreenHeartbeat = async (payload: {
    screenId: string;
    hospitalId: string;
    screenName: string;
    handle: string;
    userAgent?: string;
    resolution?: string;
    theme?: string;
}): Promise<{ success: boolean; config?: { forcedTheme?: string } }> => {
    try {
        const res = await apiFetch(`${API_BASE}/screens/heartbeat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!res.ok) return { success: false };
        return await res.json();
    } catch {
        return { success: false };
    }
};

export const updateScreenSession = async (screenId: string, data: { screenName?: string; forcedTheme?: string | null; screenGroup?: string | null; screenMode?: string; screenModeData?: string | null; forceRefreshTs?: number | null }): Promise<boolean> => {
    const res = await apiFetch(`${API_BASE}/screens/${screenId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
    });
    if (handleAuthError(res)) return false;
    if (!res.ok) return false;
    const result = await res.json();
    return result.success;
};

export const subscribeToScreenConfig = (screenId: string, onConfig: (config: any) => void) => {
    return createResilientSSE(`${API_BASE}/screens/${screenId}/config-stream`, onConfig);
};

// ============================================================
// 🔒 TV PAIRING (ZERO-TRUST)
// ============================================================

export const requestScreenPairing = async (hospitalId: string): Promise<{ code?: string, expiresAt?: number }> => {
    try {
        const res = await apiFetch(`${import.meta.env.VITE_API_BASE || ''}/api/screens/pairing/request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hospitalId })
        });
        if (!res.ok) return {};
        return await res.json();
    } catch {
        return {};
    }
};

export const checkScreenPairingStatus = async (code: string): Promise<{ status: string, screenToken?: string, screenId?: string }> => {
    try {
        const res = await apiFetch(`${import.meta.env.VITE_API_BASE || ''}/api/screens/pairing/status/${code}`);
        if (!res.ok) return { status: 'PENDING' };
        return await res.json();
    } catch {
        return { status: 'PENDING' };
    }
};

export const approveScreenPairing = async (code: string): Promise<boolean> => {
    try {
        const res = await apiFetch(`${import.meta.env.VITE_API_BASE || ''}/api/screens/pairing/approve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code })
        });
        return res.ok;
    } catch {
        return false;
    }
};

// ============================================================
// ⭐ PATIENT FEEDBACK — Post-surgery feedback collection
// ============================================================

export const submitFeedback = async (data: {
    hospitalId: string;
    rating: number;
    message?: string;
    patientCode?: string;
}): Promise<boolean> => {
    try {
        const res = await apiFetch(`${API_BASE}/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        return res.ok;
    } catch {
        return false;
    }
};

export const getFeedback = async (hospitalId: string): Promise<any[]> => {
    const res = await apiFetch(`${API_BASE}/feedback?hospitalId=${hospitalId}`, { credentials: 'include' });
    if (!res.ok) {
        if (res.status === 401 || res.status === 403) throw new Error('UNAUTHORIZED');
        return [];
    }
    return await res.json();
};

export const getFeedbackStats = async (hospitalId: string): Promise<any | null> => {
    const res = await apiFetch(`${API_BASE}/feedback/stats?hospitalId=${hospitalId}`, { credentials: 'include' });
    if (!res.ok) {
        if (res.status === 401 || res.status === 403) throw new Error('UNAUTHORIZED');
        return null;
    }
    return await res.json();
};

export const trackReviewEvent = async (data: {
    hospitalId: string;
    patientId?: string;
    eventType: 'review_shown' | 'google_review_click' | 'review_dismissed';
    rating?: number;
}): Promise<void> => {
    // Fire-and-forget: don't block the UI
    try {
        fetch(`${API_BASE}/feedback/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).catch(() => {});
    } catch {}
};

// ============================================================
// 👥 STAFF SESSIONS (Active Sessions Dashboard)
// ============================================================

export const getStaffSessions = async (hospitalId?: string): Promise<any[]> => {
    const url = hospitalId
        ? `${API_BASE}/sessions?hospitalId=${hospitalId}`
        : `${API_BASE}/sessions`;
    const res = await apiFetch(url, { credentials: 'include' });
    if (res.status === 401) throw new Error('UNAUTHORIZED');
    if (!res.ok) return [];
    return await res.json();
};

export const revokeStaffSession = async (sessionId: string): Promise<boolean> => {
    const res = await apiFetch(`${API_BASE}/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (res.status === 401) throw new Error('UNAUTHORIZED');
    return res.ok;
};
